/**
 * Admin Support Tickets - Support ticket management
 */
import { useLanguage } from "@/context/AppContext";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  ChevronLeft, Search, MessageSquare, Send, Loader2, Clock, CheckCircle, AlertCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSupportTickets = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [reply, setReply] = useState('');

  useEffect(() => {
    fetchTickets();
  }, [statusFilter]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      // Support tickets endpoint - may not exist yet
      const res = await axios.get(`${API}/admin/support-tickets`, { withCredentials: true });
      setTickets(res.data.tickets || []);
    } catch (error) {
      // Mock data for now since endpoint doesn't exist
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!reply.trim()) return;
    
    try {
      await axios.post(`${API}/admin/support-tickets/${selectedTicket.id}/reply`, 
        { message: reply }, 
        { withCredentials: true }
      );
      toast.success('Antwort gesendet');
      setReply('');
      setShowTicketModal(false);
      fetchTickets();
    } catch (error) {
      toast.error('Fehler beim Senden');
    }
  };

  const closeTicket = async (ticketId) => {
    try {
      await axios.post(`${API}/admin/support-tickets/${ticketId}/close`, {}, { withCredentials: true });
      toast.success('Ticket geschlossen');
      fetchTickets();
    } catch (error) {
      toast.error('Fehler beim Schließen');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'in_progress': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'resolved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen infirmly-dark bg-gray-100" style={{ background: "radial-gradient(ellipse at top, #0F2557 0%, #0A1A3E 60%, #050B23 100%)" }} data-testid="admin-support-tickets">
      {/* Header */}
      <header className="border-b border-white/10 sticky bg-white/[0.04] backdrop-blur top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')} className="rounded-full">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('adminPageSupport')}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Ticket suchen..."
                  className="pl-10 rounded-full"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48 rounded-full">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Tickets</SelectItem>
                  <SelectItem value="open">Offen</SelectItem>
                  <SelectItem value="in_progress">In Bearbeitung</SelectItem>
                  <SelectItem value="resolved">Gelöst</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tickets List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Tickets ({tickets.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Keine Support-Tickets vorhanden</p>
                <p className="text-sm mt-2">Neue Tickets erscheinen hier automatisch</p>
              </div>
            ) : (
              <div className="divide-y">
                {tickets.map((ticket) => (
                  <div 
                    key={ticket.id} 
                    className="py-4 flex items-center justify-between hover:bg-gray-50 px-2 rounded-lg cursor-pointer"
                    onClick={() => { setSelectedTicket(ticket); setShowTicketModal(true); }}
                  >
                    <div className="flex items-center gap-4">
                      {getStatusIcon(ticket.status)}
                      <div>
                        <p className="font-medium">{ticket.subject}</p>
                        <p className="text-sm text-gray-500">{ticket.user_name} • {ticket.user_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">
                        {new Date(ticket.created_at).toLocaleDateString('de-CH')}
                      </span>
                      <Badge variant={ticket.status === 'open' ? 'destructive' : 'secondary'}>
                        {ticket.status === 'open' ? 'Offen' : ticket.status === 'in_progress' ? 'In Bearbeitung' : 'Gelöst'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Ticket Detail Modal */}
      <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedTicket?.subject}</DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-500 mb-2">
                  Von: {selectedTicket.user_name} ({selectedTicket.user_email})
                </p>
                <p>{selectedTicket.message}</p>
              </div>
              
              <div>
                <Label>Antwort</Label>
                <Textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Ihre Antwort..."
                  rows={4}
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTicketModal(false)}>
              Schließen
            </Button>
            <Button onClick={handleReply} className="bg-[#0044CC] hover:bg-[#003399]">
              <Send className="w-4 h-4 mr-2" /> Antworten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSupportTickets;
